/**
 * Sistema de feedback sonoro para VYRA
 * Sons sutis e não invasivos para reforço dopaminérgico em TDAH
 */

class SoundManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  constructor() {
    // Inicializar AudioContext apenas no primeiro uso
    if (typeof window !== 'undefined') {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  private async resumeContext() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Som de conclusão de tarefa - Frequências harmoniosas
   */
  async playTaskComplete() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Tom agradável (C5 → E5)
    oscillator.frequency.setValueAtTime(523.25, now); // C5
    oscillator.frequency.exponentialRampToValueAtTime(659.25, now + 0.1); // E5

    // Envelope suave
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.15, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

    oscillator.start(now);
    oscillator.stop(now + 0.3);
  }

  /**
   * Som de captura - Feedback rápido
   */
  async playCapture() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Tom rápido e sutil
    oscillator.frequency.setValueAtTime(800, now);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.08, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    oscillator.start(now);
    oscillator.stop(now + 0.15);
  }

  /**
   * Som de foco iniciado - Tom motivador
   */
  async playFocusStart() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    
    // Acorde arpejado (C-E-G)
    const notes = [523.25, 659.25, 783.99];
    
    notes.forEach((freq, i) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.setValueAtTime(freq, now + i * 0.08);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, now + i * 0.08);
      gainNode.gain.linearRampToValueAtTime(0.1, now + i * 0.08 + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.25);

      oscillator.start(now + i * 0.08);
      oscillator.stop(now + i * 0.08 + 0.25);
    });
  }

  /**
   * Som de conquista - Celebração
   */
  async playAchievement() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    
    // Sequência ascendente feliz
    const melody = [523.25, 659.25, 783.99, 1046.50]; // C5-E5-G5-C6
    
    melody.forEach((freq, i) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.setValueAtTime(freq, now + i * 0.12);
      oscillator.type = 'triangle';

      gainNode.gain.setValueAtTime(0, now + i * 0.12);
      gainNode.gain.linearRampToValueAtTime(0.12, now + i * 0.12 + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.3);

      oscillator.start(now + i * 0.12);
      oscillator.stop(now + i * 0.12 + 0.3);
    });
  }

  /**
   * Som de pausa - Tom relaxante
   */
  async playBreak() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Tom descendente suave
    oscillator.frequency.setValueAtTime(440, now); // A4
    oscillator.frequency.exponentialRampToValueAtTime(220, now + 0.4); // A3
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.1, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);

    oscillator.start(now);
    oscillator.stop(now + 0.4);
  }

  /**
   * Som de erro - Tom dissonante e curto
   */
  async playError() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Tom descendente dissonante
    oscillator.frequency.setValueAtTime(300, now);
    oscillator.frequency.exponentialRampToValueAtTime(150, now + 0.15);
    oscillator.type = 'sawtooth';

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.1, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    oscillator.start(now);
    oscillator.stop(now + 0.15);
  }

  /**
   * Som de navegação - Tom sutil
   */
  async playNavigation() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Tom curto e sutil
    oscillator.frequency.setValueAtTime(600, now);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.05, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

    oscillator.start(now);
    oscillator.stop(now + 0.1);
  }

  /**
   * Som de milestone - Versão ampliada do achievement
   */
  async playMilestone() {
    if (!this.enabled || !this.audioContext) return;
    
    await this.resumeContext();
    
    const now = this.audioContext.currentTime;
    
    // Sequência mais longa e grandiosa
    const melody = [523.25, 659.25, 783.99, 1046.50, 1318.51]; // C5-E5-G5-C6-E6
    
    melody.forEach((freq, i) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.setValueAtTime(freq, now + i * 0.15);
      oscillator.type = 'triangle';

      gainNode.gain.setValueAtTime(0, now + i * 0.15);
      gainNode.gain.linearRampToValueAtTime(0.15, now + i * 0.15 + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.4);

      oscillator.start(now + i * 0.15);
      oscillator.stop(now + i * 0.15 + 0.4);
    });
  }
}

// Instância única
export const soundManager = new SoundManager();

/**
 * Helper function para simplificar o uso de sons
 */
export const playSound = async (soundType: 'complete' | 'capture' | 'focus' | 'achievement' | 'break' | 'swipe' | 'error' | 'navigation' | 'milestone') => {
  switch (soundType) {
    case 'complete':
      return soundManager.playTaskComplete();
    case 'capture':
      return soundManager.playCapture();
    case 'focus':
      return soundManager.playFocusStart();
    case 'achievement':
      return soundManager.playAchievement();
    case 'break':
      return soundManager.playBreak();
    case 'swipe':
      return soundManager.playCapture();
    case 'error':
      return soundManager.playError();
    case 'navigation':
      return soundManager.playNavigation();
    case 'milestone':
      return soundManager.playMilestone();
    default:
      console.warn(`Sound type "${soundType}" not found`);
  }
};
